import { patientAgeOn, type PatientAge } from '@osteopro/shared';
import type { TFunction } from 'i18next';

/** Mises en forme partagées par les écrans. Aucune ne contient de texte d'interface. */

const AGE_KEYS = {
  days: 'patients:list.age_days',
  months: 'patients:list.age_months',
  years: 'patients:list.age_years',
} as const;

/**
 * Âge d'un patient aujourd'hui, à Luxembourg, selon la règle de `patientAgeAt` : jours, mois
 * jusqu'à deux ans, puis années. `null` si la date de naissance est absente, illisible ou future.
 */
export function patientAgeLabel(
  t: TFunction,
  birthDate: string | null,
  now: Date = new Date(),
): string | null {
  return patientAgeText(t, patientAgeOn(birthDate, now));
}

/** Âge déjà calculé par `patientAgeAt`, rendu dans la langue courante. */
export function patientAgeText(t: TFunction, age: PatientAge | null): string | null {
  return age === null ? null : t(AGE_KEYS[age.unit], { count: age.value });
}

/** Instant ISO → `YYYY-MM-DDTHH:MM` dans le fuseau du navigateur. */
export function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Date `YYYY-MM-DD` rendue dans la langue courante. */
export function formatDate(value: string | null, language: string): string {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

/** Date et heure murales du cabinet (`Europe/Luxembourg`), quel que soit le fuseau du navigateur. */
export function formatCabinetDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Luxembourg',
  }).format(date);
}

/**
 * « il y a 5 minutes », « hier »… dans la langue courante, mesuré depuis `reference`. Minutes sous
 * l'heure, heures sous deux jours, jours au-delà : `Intl` porte les mots, aucune traduction à tenir.
 */
export function formatRelativeTime(iso: string, reference: string, language: string): string {
  const elapsedMinutes = Math.round((Date.parse(reference) - Date.parse(iso)) / 60_000);
  if (Number.isNaN(elapsedMinutes)) return '';
  const relative = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (Math.abs(elapsedMinutes) < 60) return relative.format(-elapsedMinutes, 'minute');
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (Math.abs(elapsedHours) < 48) return relative.format(-elapsedHours, 'hour');
  return relative.format(-Math.round(elapsedHours / 24), 'day');
}

export function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.lastName} ${person.firstName}`.trim();
}

export function initials(person: { firstName?: string | null; lastName?: string | null }): string {
  const letters = [person.firstName?.[0], person.lastName?.[0]].filter(Boolean).join('');
  return letters.toUpperCase() || '?';
}

/**
 * Une taille en octets, rendue lisible. Les symboles d'unité sont ceux du Système international
 * et ne se traduisent pas ; seul le séparateur décimal suit la langue.
 *
 * Base 1000 et non 1024 : c'est la convention de tous les hébergeurs, et c'est en gigaoctets
 * décimaux qu'un quota de stockage se facture.
 */
export function formatBytes(bytes: number, language: string): string {
  const units = ['o', 'ko', 'Mo', 'Go', 'To'];
  let value = Math.max(bytes, 0);
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  const formatted = new Intl.NumberFormat(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
  return `${formatted} ${units[unit] ?? 'o'}`;
}

/** Robustesse d'un mot de passe, de 0 à 4. Sert uniquement à l'indicateur visuel. */
export function passwordStrength(password: string): number {
  if (password.length === 0) return 0;
  let score = 0;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password) && /[^\w\s]/.test(password)) score += 1;
  return Math.min(score, 4);
}
